import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import {
  Car, AlertTriangle, Wrench, RefreshCw,
  Activity, Truck, RotateCcw, Clock, AlertCircle, DollarSign, TrendingUp, TrendingDown,
  CheckCircle2, ChevronRight, FileSignature, MessageCircle,
} from 'lucide-react';
import { FarmoutsCard, PayrollPreviewCard } from './DashboardOpsCards';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt$ = (n) => `$${Number(n || 0).toLocaleString()}`;

const SectionLabel = ({ children }) => (
  <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">{children}</p>
);

// Alert tile: red/amber when count > 0, subdued "all clear" when 0. Always clickable.
const AttentionTile = ({ icon: Icon, label, value, severity = 'amber', onClick, testid }) => {
  const hot = Number(value) > 0;
  const cls = hot
    ? severity === 'red'
      ? 'bg-red-950/40 border-red-800/60 hover:border-red-600'
      : 'bg-amber-950/30 border-amber-800/50 hover:border-amber-600'
    : 'bg-gray-950 border-gray-800 hover:border-gray-700';
  const iconCls = hot ? (severity === 'red' ? 'text-red-400 bg-red-500/15' : 'text-amber-400 bg-amber-500/15') : 'text-emerald-500 bg-emerald-500/10';
  const valCls = hot ? (severity === 'red' ? 'text-red-400' : 'text-amber-400') : 'text-gray-500';
  return (
    <Card onClick={onClick} className={`p-4 cursor-pointer transition-colors ${cls}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold leading-tight">{label}</p>
        <div className={`h-7 w-7 rounded-md flex items-center justify-center flex-shrink-0 ${iconCls.split(' ')[1]}`}>
          {hot ? <Icon className={`h-3.5 w-3.5 ${iconCls.split(' ')[0]}`} /> : <CheckCircle2 className={`h-3.5 w-3.5 ${iconCls.split(' ')[0]}`} />}
        </div>
      </div>
      <p className={`text-3xl font-black mt-2 ${valCls}`}>{value}</p>
      <p className="text-[9px] text-gray-600 font-bold uppercase tracking-wider mt-1 flex items-center gap-0.5">
        {hot ? 'View queue' : 'All clear'} <ChevronRight className="h-2.5 w-2.5" />
      </p>
    </Card>
  );
};

const ActivityTile = ({ icon: Icon, label, value, color = 'blue', onClick, testid }) => {
  const COLOR = {
    red: 'text-red-500 bg-red-500/10',
    blue: 'text-blue-500 bg-blue-500/10',
    purple: 'text-purple-500 bg-purple-500/10',
  };
  return (
    <Card onClick={onClick} className="p-4 bg-gray-950 border-gray-800 hover:border-gray-700 cursor-pointer transition-colors hover:bg-gray-900" data-testid={testid}>
      <div className="flex items-start justify-between">
        <p className="text-[9px] uppercase tracking-widest text-gray-500 font-bold leading-tight">{label}</p>
        <div className={`h-7 w-7 rounded-md ${COLOR[color].split(' ')[1]} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-3.5 w-3.5 ${COLOR[color].split(' ')[0]}`} />
        </div>
      </div>
      <p className="text-3xl font-black text-white mt-2">{value}</p>
    </Card>
  );
};

const Sparkline = ({ points, color = '#10b981' }) => {
  if (!points || points.length < 2) return null;
  const max = Math.max(...points, 1);
  const w = 140, h = 40;
  const step = w / (points.length - 1);
  const d = points.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i * step).toFixed(1)},${(h - (v / max) * (h - 6) - 3).toFixed(1)}`).join(' ');
  return (
    <svg width={w} height={h} className="overflow-visible">
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((v, i) => i === points.length - 1 && (
        <circle key={i} cx={(i * step).toFixed(1)} cy={(h - (v / max) * (h - 6) - 3).toFixed(1)} r="3" fill={color} />
      ))}
    </svg>
  );
};

const DeltaChip = ({ current, previous, suffix = '' }) => {
  const prev = Number(previous) || 0;
  const cur = Number(current) || 0;
  if (!prev && !cur) return <span className="text-[10px] text-gray-600 font-bold">no data yet</span>;
  const diff = cur - prev;
  const pct = prev ? Math.round((diff / prev) * 100) : 100;
  const up = diff >= 0;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${up ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'}`}>
      {up ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {up ? '+' : ''}{pct}% vs last month{suffix}
    </span>
  );
};

const StatusTile = ({ label, value, color }) => {
  const COLOR = { green: 'text-emerald-400', amber: 'text-amber-400', blue: 'text-blue-400', orange: 'text-orange-400', red: 'text-red-400' };
  return (
    <Card className="p-4 bg-gray-950 border-gray-800 text-center">
      <p className={`text-3xl font-black ${COLOR[color] || 'text-gray-300'}`}>{value}</p>
      <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mt-1">{label}</p>
    </Card>
  );
};

const HealthRing = ({ score, label }) => {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = score >= 90 ? '#10b981' : score >= 75 ? '#84cc16' : score >= 60 ? '#f59e0b' : '#ef4444';
  return (
    <Card className="p-6 bg-emerald-950/30 border-emerald-900/50" data-testid="health-ring">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-2">Fleet Health Score</p>
          <p className="text-6xl font-black text-emerald-400 leading-none">{score}</p>
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-300 mt-2">{label}</p>
        </div>
        <svg width="120" height="120" className="-rotate-90">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="#1f2937" strokeWidth="8" />
          <circle
            cx="60" cy="60" r={radius} fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={circumference} strokeDashoffset={offset} strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s ease-out' }}
          />
          <text x="60" y="60" textAnchor="middle" dy="0.35em" fill={color} transform="rotate(90 60 60)" style={{ fontSize: '24px', fontWeight: 900 }}>
            {score}
          </text>
        </svg>
      </div>
    </Card>
  );
};

const OpsDashboardTab = ({ onJumpTab }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const r = await fetch(`${API}/api/command-center/ops-dashboard`);
      const d = await r.json();
      setData(d);
    } catch (_) {}
    setLoading(false); setRefreshing(false);
  }, []);

  useEffect(() => { load(); const t = setInterval(() => load(true), 60000); return () => clearInterval(t); }, [load]);

  if (loading) return (
    <div className="bg-black min-h-[600px] -m-6 p-6 flex items-center justify-center" data-testid="ops-dashboard-loading">
      <p className="text-gray-500">Loading dashboard…</p>
    </div>
  );

  const k = data?.kpis || {};
  const fs = data?.fleet_status || {};
  const fh = data?.fleet_health || { score: 0, label: '' };
  const trends = data?.trends || {};
  const revSeries = (trends.revenue_by_month || []).map(m => m.revenue);
  const jump = (tab, anchor) => {
    onJumpTab && onJumpTab(tab);
    if (anchor) setTimeout(() => document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
  };
  const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const attentionCount = (k.pending_approvals || 0) + (k.open_incidents || 0) + (k.maintenance_due || 0) + (k.payouts_pending || 0) + (k.compliance_issues || 0) + (k.waiting_on_client || 0);

  return (
    <div className="bg-black -m-6 p-5 sm:p-6 space-y-6 min-h-screen text-white" data-testid="ops-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight uppercase">Operations Dashboard</h1>
          <p className="text-[11px] text-gray-500 uppercase tracking-widest font-bold mt-1">Your Auto Concierge · {today}</p>
        </div>
        <button onClick={() => load(true)} className="text-gray-400 hover:text-white" data-testid="ops-dashboard-refresh">
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* ── Needs Attention ── */}
      <div className="space-y-3" data-testid="needs-attention-section">
        <div className="flex items-center gap-2">
          <AlertTriangle className={`h-3.5 w-3.5 ${attentionCount > 0 ? 'text-amber-400' : 'text-emerald-500'}`} />
          <SectionLabel>Needs Attention</SectionLabel>
          {attentionCount === 0 && <span className="text-[10px] text-emerald-500 font-bold uppercase tracking-wider">· all clear</span>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <AttentionTile icon={Clock} label="Pending Approvals" value={k.pending_approvals || 0} severity="amber" onClick={() => jump('quotebookings', 'rental-requests-anchor')} testid="kpi-pending" />
          <AttentionTile icon={MessageCircle} label="Waiting on Client" value={k.waiting_on_client || 0} severity="amber" onClick={() => jump('quotebookings', 'requests-inbox-anchor')} testid="kpi-waiting" />
          <AttentionTile icon={AlertCircle} label="Open Incidents" value={k.open_incidents || 0} severity="red" onClick={() => jump('weeklypl', 'condition-reports-anchor')} testid="kpi-incidents" />
          <AttentionTile icon={Wrench} label="Maintenance Due" value={k.maintenance_due || 0} severity="amber" onClick={() => jump('weeklypl', 'maintenance-anchor')} testid="kpi-maintenance" />
          <AttentionTile icon={DollarSign} label="Payouts Pending" value={k.payouts_pending || 0} severity="amber" onClick={() => jump('teampay', 'owner-payouts-anchor')} testid="kpi-payouts" />
          <AttentionTile icon={FileSignature} label="Unsigned Contracts" value={k.compliance_issues || 0} severity="red" onClick={() => jump('contracts')} testid="kpi-compliance" />
        </div>
      </div>

      {/* ── Today's Activity ── */}
      <div className="space-y-3" data-testid="todays-activity-section">
        <div className="flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-blue-400" />
          <SectionLabel>Today's Activity</SectionLabel>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <ActivityTile icon={Car} label="Vehicles Out" value={k.vehicles_out || 0} color="red" onClick={() => jump('dailyops')} testid="kpi-vehicles-out" />
          <ActivityTile icon={Truck} label="Today's Pickups" value={k.todays_pickups || 0} color="blue" onClick={() => jump('quotebookings')} testid="kpi-pickups" />
          <ActivityTile icon={RotateCcw} label="Today's Returns" value={k.todays_returns || 0} color="purple" onClick={() => jump('quotebookings')} testid="kpi-returns" />
        </div>

        {/* Revenue + Utilization with trend context */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Card onClick={() => jump('monthlypl')} className="p-5 bg-gray-950 border-gray-800 hover:border-gray-700 cursor-pointer transition-colors" data-testid="card-revenue">
            <div className="flex items-start justify-between">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Revenue This Month</p>
              <div className="h-8 w-8 rounded-md bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="h-4 w-4 text-emerald-500" />
              </div>
            </div>
            <div className="flex items-end justify-between gap-3 mt-3">
              <div>
                <p className="text-4xl sm:text-5xl font-black text-white">{fmt$(k.revenue_month)}</p>
                <div className="mt-2"><DeltaChip current={k.revenue_month} previous={trends.revenue_last_month} /></div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <Sparkline points={revSeries} color="#10b981" />
                <p className="text-[9px] text-gray-600 font-bold uppercase tracking-wider">6-month trend</p>
              </div>
            </div>
          </Card>
          <Card onClick={() => jump('weeklypl')} className="p-5 bg-gray-950 border-gray-800 hover:border-gray-700 cursor-pointer transition-colors" data-testid="card-utilization">
            <div className="flex items-start justify-between">
              <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Fleet Utilization</p>
              <div className="h-8 w-8 rounded-md bg-red-500/10 flex items-center justify-center">
                <TrendingUp className="h-4 w-4 text-red-500" />
              </div>
            </div>
            <p className="text-4xl sm:text-5xl font-black text-white mt-3">{k.fleet_utilization_pct || 0}%</p>
            <div className="mt-2 flex items-center gap-2">
              <DeltaChip current={k.fleet_utilization_pct} previous={trends.utilization_last_month_pct} />
              <span className="text-[10px] text-gray-600 font-bold">last month: {trends.utilization_last_month_pct || 0}%</span>
            </div>
          </Card>
        </div>
      </div>

      {/* Health + Status breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <HealthRing score={fh.score} label={fh.label} />
        </div>
        <Card className="p-4 bg-gray-950 border-gray-800" data-testid="fleet-summary-card">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-3">Fleet Snapshot</p>
          <div className="grid grid-cols-2 gap-2">
            <StatusTile label="Available" value={fs.available || 0} color="green" />
            <StatusTile label="Reserved" value={fs.reserved || 0} color="amber" />
            <StatusTile label="Active" value={fs.active_rental || 0} color="blue" />
            <StatusTile label="Maintenance" value={fs.maintenance || 0} color="orange" />
          </div>
        </Card>
      </div>

      {/* Farm-outs + Payroll preview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <FarmoutsCard onJumpTab={onJumpTab} />
        <PayrollPreviewCard onJumpTab={onJumpTab} />
      </div>

      {/* Upcoming today */}
      {data?.upcoming_today?.length > 0 && (
        <Card className="bg-gray-950 border-gray-800 overflow-hidden" data-testid="upcoming-today-card">
          <div className="p-4 border-b border-gray-800 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Today's Pickups ({data.upcoming_today.length})</p>
            <button onClick={() => jump('quotebookings')} className="text-[10px] text-red-500 font-bold flex items-center gap-1 hover:text-red-400">
              View All <ChevronRight className="h-3 w-3" />
            </button>
          </div>
          <div className="divide-y divide-gray-800">
            {data.upcoming_today.map((b, i) => (
              <div key={b.booking_id || i} onClick={() => jump('quotebookings')} className="px-4 py-3 flex items-center gap-3 hover:bg-gray-900 transition-colors cursor-pointer" data-testid={`upcoming-row-${i}`}>
                <CheckCircle2 className="h-4 w-4 text-blue-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{b.client_name}</p>
                  <p className="text-[11px] text-gray-500 truncate">{b.pickup_time || '—'} · {b.vehicle || 'Vehicle TBD'}</p>
                </div>
                <p className="text-sm font-black text-emerald-400 flex-shrink-0">{fmt$(b.total)}</p>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

export default OpsDashboardTab;
